/**
 * Renova Theme JS
 * Modular, clean, and performance-focused.
 */

(function() {
  'use strict';

  // State flag for Theme Editor to avoid duplicate listeners
  if (window.RenovaThemeInitialized) return;
  window.RenovaThemeInitialized = true;

  // =======================================================================
  // 1. SCROLL REVEAL (IntersectionObserver)
  // =======================================================================
  function initScrollReveal() {
    // Respect user's motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const reveals = document.querySelectorAll('[data-reveal]');
    if (!reveals.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    reveals.forEach(el => observer.observe(el));
  }

  // =======================================================================
  // 2. STICKY HEADER
  // =======================================================================
  function initStickyHeader() {
    const header = document.querySelector('.header-section');
    if (!header) return;

    let isSticky = false;

    window.addEventListener('scroll', () => {
      const scrollY = window.scrollY || window.pageYOffset;
      if (scrollY > 50 && !isSticky) {
        header.classList.add('is-sticky');
        isSticky = true;
      } else if (scrollY <= 50 && isSticky) {
        header.classList.remove('is-sticky');
        isSticky = false;
      }
    }, { passive: true });
  }

  // =======================================================================
  // 3. REVIEWS MODAL
  // =======================================================================
  function initReviewsModal() {
    const openBtn = document.getElementById('open-reviews-modal');
    const modal = document.getElementById('reviews-modal');
    const closeBtn = document.getElementById('close-reviews-modal');
    const searchInput = document.getElementById('reviews-search-input');
    
    if (!openBtn || !modal || !closeBtn) return;

    // Optional: Use existing reviews in DOM for search
    const reviewCards = Array.from(modal.querySelectorAll('.sp-modal-review-card'));

    const openModal = () => {
      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('is-open');
      document.body.classList.add('tr3-review-lock');
      
      // Basic focus management
      setTimeout(() => {
        if (searchInput) searchInput.focus();
      }, 100);
    };

    const closeModal = () => {
      modal.setAttribute('aria-hidden', 'true');
      modal.classList.remove('is-open');
      document.body.classList.remove('tr3-review-lock');
      openBtn.focus();
    };

    openBtn.addEventListener('click', openModal);
    closeBtn.addEventListener('click', closeModal);

    // Close on overlay click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal();
      }
    });

    // Handle ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) {
        closeModal();
      }
    });

    // Client-side Search
    if (searchInput && reviewCards.length > 0) {
      searchInput.addEventListener('input', (e) => {
        const term = e.target.value.trim().toLowerCase();
        
        // Remove accents for better matching
        const normalize = str => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const query = normalize(term);

        reviewCards.forEach(card => {
          const searchText = card.getAttribute('data-search-text') || '';
          if (!query || normalize(searchText).includes(query)) {
            card.style.display = 'block';
          } else {
            card.style.display = 'none';
          }
        });
      });
    }
  }

  // =======================================================================
  // 4. SMOOTH SCROLL (Anchor Links)
  // =======================================================================
  function initSmoothScroll() {
    const links = document.querySelectorAll('a[href^="#"]:not([href="#"])');
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        const targetId = link.getAttribute('href');
        const targetEl = document.querySelector(targetId);
        
        if (targetEl) {
          e.preventDefault();
          targetEl.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // =======================================================================
  // 5. VIDEO OPTIMIZATION (Pause off-screen)
  // =======================================================================
  function initVideoObserver() {
    const videos = document.querySelectorAll('video');
    if (!videos.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          // Pause if out of view to save battery/cpu
          if (!entry.target.paused) {
            entry.target.pause();
          }
        }
      });
    }, { threshold: 0 });

    videos.forEach(video => observer.observe(video));
  }

  // =======================================================================
  // 6. FAQ ACCORDION
  // =======================================================================
  function initFaqAccordion() {
    const faqItems = document.querySelectorAll('.faq-item');
    if (!faqItems.length) return;

    faqItems.forEach(item => {
      const summary = item.querySelector('summary');
      const answer = item.querySelector('.faq-answer');
      if (!summary || !answer) return;
      
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        
        const isOpen = item.hasAttribute('open');
        
        if (isOpen) {
          answer.style.height = '0px';
          item.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
        } else {
          item.setAttribute('open', '');
          summary.setAttribute('aria-expanded', 'true');
          const inner = answer.querySelector('.faq-answer-inner');
          if (inner) {
            answer.style.height = inner.offsetHeight + 'px';
          }
        }
      });
      
      if (!item.hasAttribute('open')) {
        answer.style.height = '0px';
        summary.setAttribute('aria-expanded', 'false');
      } else {
        const inner = answer.querySelector('.faq-answer-inner');
        if (inner) {
          answer.style.height = inner.offsetHeight + 'px';
        }
        summary.setAttribute('aria-expanded', 'true');
      }
      answer.style.transition = 'height 0.3s ease';
    });
    
    window.addEventListener('resize', () => {
      faqItems.forEach(item => {
        if (item.hasAttribute('open')) {
          const answer = item.querySelector('.faq-answer');
          const inner = answer.querySelector('.faq-answer-inner');
          if (answer && inner) {
            answer.style.height = inner.offsetHeight + 'px';
          }
        }
      });
    }, { passive: true });
  }

  // =======================================================================
  // INIT ALL
  // =======================================================================
  function initAll() {
    initScrollReveal();
    initStickyHeader();
    initReviewsModal();
    initSmoothScroll();
    initVideoObserver();
    initFaqAccordion();
  }

  // Run on DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Handle Shopify Theme Editor events
  document.addEventListener('shopify:section:load', function(event) {
    // Re-initialize specific components if needed
    initScrollReveal();
    initFaqAccordion();
  });

})();
